import { createReducer, on } from '@ngrx/store';

import * as fromLeadsModuleModels from '../../models';
import { ErrorsObject } from '../../../shared/utilities/error-utility.utility';
import { LeadCommentsActions } from '../actions';
import { getAndHandleBackendErrors } from '../../../shared/utilities/reducer.utility';
import { UserRoleActions } from '../../../application-state/store/actions';

export interface State {
  entities: { [iri: string]: fromLeadsModuleModels.LeadComment };
  errors: ErrorsObject;
  loading: boolean;
  loaded: boolean;
  pagination?: {
    current?: string;
    first?: string;
    last?: string;
    next?: string;
    previous?: string;
  };
  totalItems: number;
}

const initialState = {
  entities: {},
  errors: {},
  loading: false,
  loaded: false,
  totalItems: 0
};

export const reducer = createReducer(
  initialState,
  on(
    LeadCommentsActions.LoadLeadComment,
    LeadCommentsActions.ReadLeadComments,
    state => {
      return {
        ...state,
        loading: true
      };
    }
  ),
  on(
    LeadCommentsActions.CreateLeadCommentSuccess,
    LeadCommentsActions.UpdateLeadCommentSuccess,
    (state, { response }) => {
      const entities = {
        ...state.entities,
        [response['@id']]: response
      };

      return {
        ...state,
        entities,
        loading: false
      };
    }
  ),
  on(LeadCommentsActions.ReadLeadCommentsSuccess, (state, { response }) => {
    const hasPagination = response.hasOwnProperty('hydra:view');
    const items = response['hydra:member'];
    let pagination = {};
    const entities = items.reduce(
      (
        entities: { [iri: string]: fromLeadsModuleModels.LeadComment },
        item
      ) => {
        return {
          ...entities,
          [item['@id']]: item
        };
      },
      // intentionally "reset" entities for proper pagination as opposed to accumulating values
      {}
    );

    // Sample
    // "@id": "/api/customers?page=1",                          ---- always available
    // "hydra:first": "/api/customers?page=1",                  ---- always available
    // "hydra:next": "/api/customers?page=2"                    ---- might be unavailable
    // "hydra:previous": "/api/customers?page=2"                ---- might be unavailable
    // "hydra:last": "/api/customers?page=2",                   ---- always available

    if (hasPagination) {
      pagination = {
        first: response['hydra:view']['hydra:first'],
        current: response['hydra:view']['@id'],
        last: response['hydra:view']['hydra:last']
      };

      if (response['hydra:view'].hasOwnProperty('hydra:previous')) {
        pagination = {
          ...pagination,
          previous: response['hydra:view']['hydra:previous']
        };
      }

      if (response['hydra:view'].hasOwnProperty('hydra:next')) {
        pagination = {
          ...pagination,
          next: response['hydra:view']['hydra:next']
        };
      }
    }

    return {
      ...state,
      entities,
      pagination,
      errors: {},
      loading: false
    };
  }),
  on(LeadCommentsActions.DeleteLeadCommentSuccess, (state, { iri }) => {
    // @ts-ignore
    const { [iri]: removed, ...entities } = state.entities;

    return {
      ...state,
      entities,
      errors: {},
      loading: false
    };
  }),
  on(
    LeadCommentsActions.LoadLeadCommentFail,
    LeadCommentsActions.ReadLeadCommentsFail,
    (state, { response }) => {
      return {
        ...state,
        errors: getAndHandleBackendErrors(response),
        loading: false
      };
    }
  ),
  on(UserRoleActions.UserRoleCheckFail, state => ({ ...state, loading: false }))
);
